import { useCallback, useEffect, useMemo, useState } from "react";
import { format, parse } from "date-fns";
import { toast } from "sonner";

import { AvailabilitySlotDTO } from "@/data/dto/theme.dto";
import {
  addonRepository,
  bookingRepository,
  packageRepository,
  themeRepository,
} from "@/data/repositories";
import { Addon } from "@/domain/entities/addon.entity";
import {
  Booking,
  BookingSlot,
  CustomerInfo,
  SelectedAddon,
} from "@/domain/entities/booking.entity";
import { Package } from "@/domain/entities/package.entity";
import { Theme } from "@/domain/entities/theme.entity";
import { formatMYR } from "@/shared/utils/currency";
import {
  BookingFlowConfig,
  BookingThemeMode,
} from "@/components/booking/booking-flow-config";

export type BookingStep =
  | "PACKAGE"
  | "THEME"
  | "DATE_TIME"
  | "ADDONS"
  | "CUSTOMER"
  | "REVIEW";

export const STEPS: BookingStep[] = [
  "PACKAGE",
  "THEME",
  "DATE_TIME",
  "ADDONS",
  "CUSTOMER",
  "REVIEW",
];

export const ASYNC_STATUS = {
  INITIAL: "initial",
  LOADING: "loading",
  FAILURE: "failure",
  SUCCESS: "success",
} as const;

export type AsyncStatus = (typeof ASYNC_STATUS)[keyof typeof ASYNC_STATUS];

export type GetPackagesStatus = AsyncStatus;
export type GetThemesStatus = AsyncStatus;
export type GetAddonsStatus = AsyncStatus;
export type GetAvailabilityStatus = AsyncStatus;
export type PostBookingStatus = AsyncStatus;

const INITIAL_CUSTOMER_INFO: CustomerInfo = {
  name: "",
  email: "",
  phone: "",
  notes: "",
};

const sortAvailabilitySlots = (slots: AvailabilitySlotDTO[]) =>
  [...slots].sort((a, b) => {
    const timeA = parse(a.time, "hh:mm a", new Date());
    const timeB = parse(b.time, "hh:mm a", new Date());
    return timeA.getTime() - timeB.getTime();
  });

const getSplitSlotCount = (selectedPackage: Package | null) => {
  if (!selectedPackage) return 1;
  if (selectedPackage.durationMinutes <= 20) return 1;
  if (selectedPackage.durationMinutes <= 40) return 2;
  return 3;
};

const getConsecutiveSlotCount = (selectedPackage: Package | null) => {
  switch (selectedPackage?.durationMinutes) {
    case 60:
      return 2;
    case 90:
      return 3;
    case 30:
    default:
      return 1;
  }
};

const isNextHalfHourSlot = (
  currentSlot: AvailabilitySlotDTO,
  nextSlot: AvailabilitySlotDTO
) => {
  const currentTime = parse(currentSlot.time, "hh:mm a", new Date());
  const nextTime = parse(nextSlot.time, "hh:mm a", new Date());

  return nextTime.getTime() - currentTime.getTime() === 30 * 60 * 1000;
};

const isCustomerInfoValid = (
  customerInfo: CustomerInfo,
  allowFlexiblePhone: boolean
) => {
  const nameValid = customerInfo.name.trim().length >= 3;
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
    customerInfo.email.trim()
  );
  const normalizedPhone = customerInfo.phone.replace(/\s+/g, "");
  const phoneValid = allowFlexiblePhone
    ? /^[+\d][\d-]{7,}$/.test(normalizedPhone)
    : /^\d+$/.test(customerInfo.phone.trim());

  return nameValid && emailValid && phoneValid;
};

const getThemeModeForPackage = (
  configThemeMode: BookingThemeMode,
  selectedPackage: Package | null
) => {
  if (configThemeMode === "single") {
    return "single" as const;
  }

  if (selectedPackage?.durationMinutes && selectedPackage.durationMinutes >= 60) {
    return "all-inclusive" as const;
  }

  return "multi" as const;
};

export const useBookingFlow = (config: BookingFlowConfig) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [selectedThemes, setSelectedThemes] = useState<Theme[]>([]);
  const [slots, setSlots] = useState<BookingSlot[]>([]);
  const [addons, setAddons] = useState<SelectedAddon[]>([]);
  const [customerInfo, setCustomerInfo] =
    useState<CustomerInfo>(INITIAL_CUSTOMER_INFO);

  const [packages, setPackages] = useState<Package[]>([]);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [addonsList, setAddonsList] = useState<Addon[]>([]);

  const [getPackagesStatus, setGetPackagesStatus] =
    useState<GetPackagesStatus>(ASYNC_STATUS.INITIAL);
  const [getThemesStatus, setGetThemesStatus] =
    useState<GetThemesStatus>(ASYNC_STATUS.INITIAL);
  const [getAddonsStatus, setGetAddonsStatus] =
    useState<GetAddonsStatus>(ASYNC_STATUS.INITIAL);

  const [errorThemes, setErrorThemes] = useState<Record<string, boolean>>({});
  const [date1, setDate1] = useState<Date | undefined>(new Date());
  const [date2, setDate2] = useState<Date | undefined>(new Date());
  const [availableSlots1, setAvailableSlots1] = useState<AvailabilitySlotDTO[]>(
    []
  );
  const [availableSlots2, setAvailableSlots2] = useState<AvailabilitySlotDTO[]>(
    []
  );
  const [getAvailability1Status, setGetAvailability1Status] =
    useState<GetAvailabilityStatus>(ASYNC_STATUS.INITIAL);
  const [getAvailability2Status, setGetAvailability2Status] =
    useState<GetAvailabilityStatus>(ASYNC_STATUS.INITIAL);
  const [dateTimeError, setDateTimeError] = useState<string | null>(null);

  const [isReviewConfirmed, setIsReviewConfirmed] = useState(false);
  const [postBookingStatus, setPostBookingStatus] =
    useState<PostBookingStatus>(ASYNC_STATUS.INITIAL);
  const [submittedBooking, setSubmittedBooking] = useState<Booking | null>(null);

  const currentStep = STEPS[currentStepIndex];
  const stepThemeMode = getThemeModeForPackage(config.themeMode, selectedPackage);

  const slotCount = useMemo(() => {
    return config.slotMode === "consecutive"
      ? getConsecutiveSlotCount(selectedPackage)
      : getSplitSlotCount(selectedPackage);
  }, [config.slotMode, selectedPackage]);

  const isConsecutiveRequired =
    config.slotMode === "consecutive" || slotCount === 3;
  const isMultiDateEnabled =
    config.slotMode === "split" && slotCount === 2;

  const resolvedSelectedThemes = useMemo(() => {
    if (stepThemeMode === "all-inclusive") {
      return themes;
    }

    if (stepThemeMode === "single") {
      return selectedThemes[0] ? [selectedThemes[0]] : [];
    }

    return selectedThemes;
  }, [selectedThemes, stepThemeMode, themes]);

  const resolvedThemePrice = useMemo(() => {
    if (!config.includeThemePriceInTotal) {
      return 0;
    }

    if (stepThemeMode === "single") {
      return resolvedSelectedThemes[0]?.price || 0;
    }

    return 0;
  }, [config.includeThemePriceInTotal, resolvedSelectedThemes, stepThemeMode]);

  const fetchPackages = useCallback(async () => {
    if (
      getPackagesStatus === ASYNC_STATUS.LOADING ||
      getPackagesStatus === ASYNC_STATUS.SUCCESS
    ) {
      return;
    }

    setGetPackagesStatus(ASYNC_STATUS.LOADING);
    try {
      const data = await packageRepository.getPackages({ module: config.module });
      setPackages(data.filter((pkg) => pkg.isActive));
      setGetPackagesStatus(ASYNC_STATUS.SUCCESS);
    } catch (error) {
      console.error("Failed to fetch packages:", error);
      setGetPackagesStatus(ASYNC_STATUS.FAILURE);
    }
  }, [config.module, getPackagesStatus]);

  const fetchThemes = useCallback(async () => {
    if (
      getThemesStatus === ASYNC_STATUS.LOADING ||
      getThemesStatus === ASYNC_STATUS.SUCCESS
    ) {
      return;
    }

    setGetThemesStatus(ASYNC_STATUS.LOADING);
    try {
      const data = await themeRepository.getThemes({ module: config.module });
      setThemes(data.filter((theme) => theme.isActive));
      setGetThemesStatus(ASYNC_STATUS.SUCCESS);
    } catch (error) {
      console.error("Failed to fetch themes:", error);
      setGetThemesStatus(ASYNC_STATUS.FAILURE);
      toast.error("Unable to load themes", {
        description:
          "The theme list could not be loaded right now. Please try again.",
      });
    }
  }, [config.module, getThemesStatus]);

  const fetchAddons = useCallback(async () => {
    if (
      getAddonsStatus === ASYNC_STATUS.LOADING ||
      getAddonsStatus === ASYNC_STATUS.SUCCESS
    ) {
      return;
    }

    setGetAddonsStatus(ASYNC_STATUS.LOADING);
    try {
      const data = await addonRepository.getAddons({ module: config.module });
      setAddonsList(data.filter((addon) => addon.isActive));
      setGetAddonsStatus(ASYNC_STATUS.SUCCESS);
    } catch (error) {
      console.error("Failed to fetch addons:", error);
      setGetAddonsStatus(ASYNC_STATUS.FAILURE);
      toast.error("Unable to load add-ons", {
        description:
          "The add-on list could not be loaded right now. Please try again.",
      });
    }
  }, [config.module, getAddonsStatus]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep, submittedBooking]);

  const fetchAvailability1 = useCallback(async () => {
    if (
      getAvailability1Status === ASYNC_STATUS.LOADING ||
      getAvailability1Status === ASYNC_STATUS.SUCCESS ||
      !date1 ||
      !selectedPackage
    ) {
      return;
    }

    const themeIdForQuery = resolvedSelectedThemes[0]?.id;
    if (!themeIdForQuery) {
      return;
    }

    setGetAvailability1Status(ASYNC_STATUS.LOADING);
    try {
      const results = await themeRepository.getAvailableTimes(
        themeIdForQuery,
        date1,
        selectedPackage.id
      );

      setAvailableSlots1(sortAvailabilitySlots(results));
      setGetAvailability1Status(ASYNC_STATUS.SUCCESS);
    } catch (error) {
      console.error("Failed to fetch available times 1:", error);
      setGetAvailability1Status(ASYNC_STATUS.FAILURE);
      toast.error("Unable to load availability", {
        description:
          "The available times for the first session could not be loaded. Please try again.",
      });
    }
  }, [
    date1,
    getAvailability1Status,
    resolvedSelectedThemes,
    selectedPackage,
  ]);

  const fetchAvailability2 = useCallback(async () => {
    if (
      getAvailability2Status === ASYNC_STATUS.LOADING ||
      getAvailability2Status === ASYNC_STATUS.SUCCESS ||
      !isMultiDateEnabled ||
      !date2 ||
      !selectedPackage
    ) {
      return;
    }

    const themeId = resolvedSelectedThemes[1]?.id || resolvedSelectedThemes[0]?.id;
    if (!themeId) {
      return;
    }

    setGetAvailability2Status(ASYNC_STATUS.LOADING);
    try {
      const results = await themeRepository.getAvailableTimes(
        themeId,
        date2,
        selectedPackage.id
      );

      setAvailableSlots2(sortAvailabilitySlots(results));
      setGetAvailability2Status(ASYNC_STATUS.SUCCESS);
    } catch (error) {
      console.error("Failed to fetch available times 2:", error);
      setGetAvailability2Status(ASYNC_STATUS.FAILURE);
      toast.error("Unable to load availability", {
        description:
          "The available times for the second session could not be loaded. Please try again.",
      });
    }
  }, [
    date2,
    getAvailability2Status,
    isMultiDateEnabled,
    resolvedSelectedThemes,
    selectedPackage,
  ]);

  const resetCustomerInfo = useCallback(() => {
    setCustomerInfo(INITIAL_CUSTOMER_INFO);
  }, []);

  const resetAvailabilityState = useCallback(() => {
    setSlots([]);
    setDate1(new Date());
    setDate2(new Date());
    setAvailableSlots1([]);
    setAvailableSlots2([]);
    setGetAvailability1Status(ASYNC_STATUS.INITIAL);
    setGetAvailability2Status(ASYNC_STATUS.INITIAL);
    setDateTimeError(null);
  }, []);

  const updatePackage = useCallback(
    (pkg: Package) => {
      setSelectedPackage(pkg);
      setSelectedThemes([]);
      setAddons([]);
      resetCustomerInfo();
      setIsReviewConfirmed(false);
      resetAvailabilityState();
    },
    [resetAvailabilityState, resetCustomerInfo]
  );

  const selectThemeForSlot = useCallback(
    (slotIndex: number, theme: Theme) => {
      setSelectedThemes((previous) => {
        if (stepThemeMode === "single") {
          return [theme];
        }

        const nextThemes = [...previous];
        nextThemes[slotIndex] = theme;
        return nextThemes;
      });

      resetAvailabilityState();
      setIsReviewConfirmed(false);
    },
    [resetAvailabilityState, stepThemeMode]
  );

  const updateAddon = useCallback((addonId: string, quantity: number) => {
    setAddons((previous) => {
      const existing = previous.find((addon) => addon.addonId === addonId);

      if (quantity <= 0) {
        return previous.filter((addon) => addon.addonId !== addonId);
      }

      if (existing) {
        return previous.map((addon) =>
          addon.addonId === addonId ? { ...addon, quantity } : addon
        );
      }

      return [...previous, { addonId, quantity }];
    });
    setIsReviewConfirmed(false);
  }, []);

  const updateCustomer = useCallback((nextInfo: CustomerInfo) => {
    setCustomerInfo(nextInfo);
    setIsReviewConfirmed(false);
  }, []);

  const goNext = useCallback(() => {
    setCurrentStepIndex((previous) =>
      previous < STEPS.length - 1 ? previous + 1 : previous
    );
  }, []);

  const goBack = useCallback(() => {
    setCurrentStepIndex((previous) => {
      if (previous === 0) {
        return previous;
      }

      const previousIndex = previous - 1;
      const previousStep = STEPS[previousIndex];

      if (previousStep === "PACKAGE") {
        setSelectedThemes([]);
        setAddons([]);
        resetCustomerInfo();
        resetAvailabilityState();
      } else if (previousStep === "THEME") {
        setAddons([]);
        resetCustomerInfo();
        resetAvailabilityState();
      } else if (previousStep === "DATE_TIME") {
        setAddons([]);
        resetCustomerInfo();
      } else if (previousStep === "ADDONS") {
        resetCustomerInfo();
      }

      setIsReviewConfirmed(false);
      return previousIndex;
    });
  }, [resetAvailabilityState, resetCustomerInfo]);

  const isDateBlocked = useCallback((date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    if (year === 2026 && month === 2) {
      return day >= 24 && day <= 26;
    }

    return false;
  }, []);

  const handleDate1Change = useCallback(
    (newDate: Date | undefined) => {
      setDate1(newDate);
      setAvailableSlots1([]);
      setGetAvailability1Status(ASYNC_STATUS.INITIAL);
      setDateTimeError(null);

      if (config.slotMode === "consecutive") {
        setSlots([]);
        return;
      }

      if (isMultiDateEnabled) {
        setSlots((previous) =>
          previous.filter((slot) => slot.sessionIndex === 1)
        );
        return;
      }

      setSlots([]);
    },
    [config.slotMode, isMultiDateEnabled]
  );

  const handleDate2Change = useCallback(
    (newDate: Date | undefined) => {
      setDate2(newDate);
      setAvailableSlots2([]);
      setGetAvailability2Status(ASYNC_STATUS.INITIAL);
      setDateTimeError(null);

      if (isMultiDateEnabled) {
        setSlots((previous) =>
          previous.filter((slot) => slot.sessionIndex === 0)
        );
      }
    },
    [isMultiDateEnabled]
  );

  const handleConsecutiveSelect = useCallback(
    (timeDTO: AvailabilitySlotDTO) => {
      if (!date1 || !resolvedSelectedThemes[0]) {
        return;
      }

      const currentIndex = availableSlots1.findIndex(
        (slot) => slot.time === timeDTO.time
      );
      if (currentIndex === -1) {
        return;
      }

      const chosenSlots = availableSlots1.slice(currentIndex, currentIndex + slotCount);

      if (chosenSlots.length < slotCount) {
        setDateTimeError(
          "There are not enough later slots to complete this package. Please choose an earlier start time or another date."
        );
        setSlots([]);
        return;
      }

      const hasGap = chosenSlots.some((slot, index) => {
        if (index === 0) {
          return false;
        }

        return !isNextHalfHourSlot(chosenSlots[index - 1], slot);
      });

      if (hasGap) {
        setDateTimeError(
          "The next consecutive 30-minute slots are not available from this start time. Please choose another time."
        );
        setSlots([]);
        return;
      }

      const anyUnavailable = chosenSlots.some((slot) => !slot.available);
      if (anyUnavailable) {
        setDateTimeError(
          "One or more required consecutive slots are already booked. Please choose another time."
        );
        setSlots([]);
        return;
      }

      const dateString = format(date1, "yyyy-MM-dd");
      setSlots(
        chosenSlots.map((slot, index) => ({
          date: dateString,
          time: slot.time,
          themeId: slot.themeId || resolvedSelectedThemes[0].id,
          available: slot.available,
          sessionIndex: index,
        }))
      );
      setDateTimeError(null);
      setIsReviewConfirmed(false);
    },
    [availableSlots1, date1, resolvedSelectedThemes, slotCount]
  );

  const handleTimeSelect = useCallback(
    (timeDTO: AvailabilitySlotDTO, section: 1 | 2) => {
      setDateTimeError(null);

      if (config.slotMode === "consecutive") {
        handleConsecutiveSelect(timeDTO);
        return;
      }

      let nextSlots: BookingSlot[] = [...slots];

      if (slotCount === 3) {
        if (!date1 || !resolvedSelectedThemes[0]) return;

        const currentIndex = availableSlots1.findIndex(
          (slot) => slot.time === timeDTO.time
        );
        if (currentIndex === -1) return;

        const chosenSlots = availableSlots1.slice(currentIndex, currentIndex + 3);

        if (chosenSlots.length < 3) {
          setDateTimeError(
            "Not enough consecutive slots available from this starting time."
          );
          return;
        }

        const anyUnavailable = chosenSlots.some((slot) => !slot.available);
        const dateString = format(date1, "yyyy-MM-dd");

        nextSlots = chosenSlots.map((slot, index) => ({
          date: dateString,
          time: slot.time,
          themeId: slot.themeId || resolvedSelectedThemes[0].id,
          available: slot.available,
          sessionIndex: index,
        }));

        if (anyUnavailable) {
          setDateTimeError(
            "One or more of the selected consecutive slots is unavailable."
          );
        }
      } else if (isMultiDateEnabled) {
        const dateString1 = date1 ? format(date1, "yyyy-MM-dd") : "";
        const dateString2 = date2 ? format(date2, "yyyy-MM-dd") : "";

        let slotOne = slots.find((slot) => slot.sessionIndex === 0);
        let slotTwo = slots.find((slot) => slot.sessionIndex === 1);

        if (section === 1 && resolvedSelectedThemes[0]) {
          slotOne = {
            date: dateString1,
            time: timeDTO.time,
            themeId: resolvedSelectedThemes[0].id,
            available: timeDTO.available,
            sessionIndex: 0,
          };
        }

        if (section === 2 && resolvedSelectedThemes[0]) {
          slotTwo = {
            date: dateString2,
            time: timeDTO.time,
            themeId:
              resolvedSelectedThemes[1]?.id || resolvedSelectedThemes[0].id,
            available: timeDTO.available,
            sessionIndex: 1,
          };
        }

        nextSlots = [];
        if (slotOne) nextSlots.push(slotOne);
        if (slotTwo) nextSlots.push(slotTwo);

        if (
          slotOne &&
          slotTwo &&
          slotOne.date === slotTwo.date &&
          slotOne.time === slotTwo.time
        ) {
          setDateTimeError(
            "You cannot select the same time for both sessions."
          );
        }

        if (!timeDTO.available) {
          setDateTimeError(
            "This slot is already booked. Please choose another one."
          );
        }
      } else {
        if (!date1 || !resolvedSelectedThemes[0]) return;

        nextSlots = [
          {
            date: format(date1, "yyyy-MM-dd"),
            time: timeDTO.time,
            themeId: resolvedSelectedThemes[0].id,
            available: timeDTO.available,
            sessionIndex: 0,
          },
        ];

        if (!timeDTO.available) {
          setDateTimeError(
            "This slot is already booked. Please choose another one."
          );
        }
      }

      setSlots(nextSlots);
      setIsReviewConfirmed(false);
    },
    [
      availableSlots1,
      config.slotMode,
      date1,
      date2,
      handleConsecutiveSelect,
      isMultiDateEnabled,
      resolvedSelectedThemes,
      slotCount,
      slots,
    ]
  );

  const isTimeSelected = useCallback(
    (time: string, section: 1 | 2) => {
      const currentDate = section === 1 ? date1 : date2;
      if (!currentDate) return false;

      const currentDateString = format(currentDate, "yyyy-MM-dd");

      if (isConsecutiveRequired && section === 1) {
        return slots.some((slot) => slot.time === time && slot.date === currentDateString);
      }

      const targetSessionIndex = section - 1;
      const currentSlot = slots.find((slot) => slot.sessionIndex === targetSessionIndex);

      if (!currentSlot) return false;

      return currentSlot.time === time && currentSlot.date === currentDateString;
    },
    [date1, date2, isConsecutiveRequired, slots]
  );

  const getTimeRangeLabel = useCallback(
    (section: 1 | 2) => {
      const targetSessionIndex = section - 1;
      const currentSlot = slots.find((slot) => slot.sessionIndex === targetSessionIndex);

      if (!currentSlot) return null;

      const startTime = currentSlot.time;
      const duration = config.slotMode === "consecutive" ? slotCount * 30 : slotCount === 3 ? 60 : 20;
      const startDate = parse(startTime, "hh:mm a", new Date());
      const endDate = new Date(startDate.getTime() + duration * 60000);

      return `${startTime} - ${format(endDate, "hh:mm a")}`;
    },
    [config.slotMode, slotCount, slots]
  );

  const selectedAddonQuantities = useMemo(
    () =>
      addons.reduce<Record<string, number>>((accumulator, addon) => {
        accumulator[addon.addonId] = addon.quantity;
        return accumulator;
      }, {}),
    [addons]
  );

  const selectedAddonsResolved = useMemo(
    () =>
      addons.map((selectedAddon) => ({
        ...selectedAddon,
        addon: addonsList.find((addon) => addon.id === selectedAddon.addonId),
      })),
    [addons, addonsList]
  );

  const packagePrice = selectedPackage?.finalPrice || 0;
  const addonsTotal = selectedAddonsResolved.reduce(
    (total, addon) => total + (addon.addon?.price || 0) * addon.quantity,
    0
  );
  const totalPrice = packagePrice + resolvedThemePrice + addonsTotal;

  const isStepValid = useMemo(() => {
    switch (currentStep) {
      case "PACKAGE":
        return !!selectedPackage;
      case "THEME":
        if (!selectedPackage) return false;
        if (stepThemeMode === "all-inclusive") return true;
        if (stepThemeMode === "single") return resolvedSelectedThemes.length === 1;
        return resolvedSelectedThemes.length > 0;
      case "DATE_TIME": {
        if (!selectedPackage) return false;
        const requiredSlots = slotCount;
        const hasCorrectCount = slots.length === requiredSlots;
        const allSlotsAvailable = slots.every(
          (slot) => !!slot.time && !!slot.date && slot.available !== false
        );
        const uniqueSlotKeys = new Set(
          slots.map((slot) => `${slot.date}-${slot.time}`)
        );
        const areSlotsUnique = uniqueSlotKeys.size === slots.length;
        return hasCorrectCount && allSlotsAvailable && areSlotsUnique;
      }
      case "CUSTOMER":
        return isCustomerInfoValid(customerInfo, config.allowFlexiblePhone);
      case "REVIEW":
        return isReviewConfirmed;
      default:
        return true;
    }
  }, [
    config.allowFlexiblePhone,
    currentStep,
    customerInfo,
    isReviewConfirmed,
    resolvedSelectedThemes.length,
    selectedPackage,
    slotCount,
    slots,
    stepThemeMode,
  ]);

  const submitBooking = useCallback(async () => {
    if (!selectedPackage || slots.length === 0) return;

    setPostBookingStatus(ASYNC_STATUS.LOADING);
    try {
      const booking: Booking = {
        packageId: selectedPackage.id,
        slots,
        addons,
        customer: customerInfo,
        status: "PENDING",
        totalPrice,
      };

      const result = await bookingRepository.createBooking(booking);
      setSubmittedBooking(result);
      setPostBookingStatus(ASYNC_STATUS.SUCCESS);
    } catch (error) {
      console.error("Failed to submit booking:", error);
      setPostBookingStatus(ASYNC_STATUS.FAILURE);
      toast.error("Unable to submit booking", {
        description:
          "Your booking could not be submitted right now. Please try again.",
      });
    }
  }, [addons, customerInfo, selectedPackage, slots, totalPrice]);

  const reset = useCallback(() => {
    setCurrentStepIndex(0);
    setSelectedPackage(null);
    setSelectedThemes([]);
    setSlots([]);
    setAddons([]);
    setCustomerInfo(INITIAL_CUSTOMER_INFO);
    setDate1(new Date());
    setDate2(new Date());
    setAvailableSlots1([]);
    setAvailableSlots2([]);
    setDateTimeError(null);
    setIsReviewConfirmed(false);
    setGetAvailability1Status(ASYNC_STATUS.INITIAL);
    setGetAvailability2Status(ASYNC_STATUS.INITIAL);
    setPostBookingStatus(ASYNC_STATUS.INITIAL);
    setSubmittedBooking(null);
  }, []);

  const isSubmitting = postBookingStatus === ASYNC_STATUS.LOADING;
  const isPackagesLoading = getPackagesStatus === ASYNC_STATUS.LOADING;
  const isThemesLoading = getThemesStatus === ASYNC_STATUS.LOADING;
  const isAddonsLoading = getAddonsStatus === ASYNC_STATUS.LOADING;
  const isLoadingSlots1 = getAvailability1Status === ASYNC_STATUS.LOADING;
  const isLoadingSlots2 = getAvailability2Status === ASYNC_STATUS.LOADING;

  const handlePrimaryAction = useCallback(() => {
    if (currentStep === "REVIEW") {
      void submitBooking();
      return;
    }

    goNext();
  }, [currentStep, goNext, submitBooking]);

  const showSummary = !!selectedPackage && currentStep !== "REVIEW";

  return {
    currentStep,
    currentStepIndex,
    totalSteps: STEPS.length,
    submittedBooking,
    showSummary,
    isStepValid,
    steps: STEPS,
    config,
    navigation: {
      goBack,
      handlePrimaryAction,
      isBackDisabled: currentStepIndex === 0 || isSubmitting,
      isPrimaryDisabled: !isStepValid || isSubmitting,
      isSubmitting,
      primaryLabel: currentStep === "REVIEW" ? "Complete Booking" : "Continue",
      showPrimaryChevron: currentStep !== "REVIEW",
    },
    status: {
      reset,
      getPackagesStatus,
      getThemesStatus,
      getAddonsStatus,
      getAvailability1Status,
      getAvailability2Status,
      postBookingStatus,
    },
    packageStep: {
      packages,
      isLoading: isPackagesLoading,
      status: getPackagesStatus,
      selectedPackage,
      onFetchPackages: fetchPackages,
      onSelect: updatePackage,
    },
    themeStep: {
      mode: stepThemeMode,
      selectedPackage,
      themes,
      isLoading: isThemesLoading,
      status: getThemesStatus,
      selectedThemes: resolvedSelectedThemes,
      errorThemes,
      slotCount,
      onFetchThemes: fetchThemes,
      onThemeImageError: (themeId: string) =>
        setErrorThemes((previous) => ({ ...previous, [themeId]: true })),
      onSelectThemeForSlot: selectThemeForSlot,
    },
    dateTimeStep: {
      slotCount,
      slotMode: config.slotMode,
      isConsecutiveRequired,
      isMultiDateEnabled,
      date1,
      date2,
      availableSlots1,
      availableSlots2,
      isLoading1: isLoadingSlots1,
      isLoading2: isLoadingSlots2,
      status1: getAvailability1Status,
      status2: getAvailability2Status,
      errorMsg: dateTimeError,
      isDateBlocked,
      onDate1Change: handleDate1Change,
      onDate2Change: handleDate2Change,
      onFetchAvailability1: fetchAvailability1,
      onFetchAvailability2: fetchAvailability2,
      onTimeSelect: handleTimeSelect,
      isTimeSelected,
      getTimeRangeLabel,
    },
    addonsStep: {
      addons: addonsList,
      isLoading: isAddonsLoading,
      status: getAddonsStatus,
      selectedAddons: addons,
      selectedAddonQuantities,
      onFetchAddons: fetchAddons,
      onUpdateAddon: updateAddon,
    },
    formStep: {
      info: customerInfo,
      onUpdate: updateCustomer,
      allowFlexiblePhone: config.allowFlexiblePhone,
      placeholders: config.customerPlaceholders,
    },
    reviewStep: {
      module: config.module,
      texts: config.texts,
      selectedPackage,
      selectedThemes: resolvedSelectedThemes,
      slots,
      selectedAddons: addons,
      customerInfo,
      addonsList,
      packagePrice,
      themePrice: resolvedThemePrice,
      totalPrice,
      isConfirmed: isReviewConfirmed,
      onConfirmedChange: setIsReviewConfirmed,
    },
    summary: {
      title: config.summaryTitle,
      badge: config.summaryBadge,
      selectedPackage,
      selectedThemes: resolvedSelectedThemes,
      slots,
      currentStep,
      selectedAddons: selectedAddonsResolved,
      packagePrice,
      themePrice: resolvedThemePrice,
      addonsTotal,
      totalPrice,
      formatPrice: formatMYR,
    },
  };
};
